// CreationModal.tsx
import { Modal, Select, Button, Switch, Spin } from 'components-antd';
import styles from './styles.module.scss';
import { CheckBox } from 'components/Icons';
import { useCallback, useEffect, useState } from 'react';
import { Input, Locations } from 'components';
import { createNewKitByAdmin, getKitsTemplatesByKitId, updateKitById } from 'api/kits';
import { FormikProvider, useFormik } from 'formik';
import FrequencyConfigurator from '../FrequenciesConfigurator';
import { showErrorMessage } from 'helpers';
import classNames from 'classnames';
import PlusIcon from '../../icons/PlusIcon';
import PenIcon from '../../icons/PenIcon';
import DetailsModal from '../DetailsModal';
import ReminderModal from '../ReminderModal';
import KitsTemplateModal from '../KitsTemplate/KitsTemplateModal';
import type { KitsTemplateModel } from '../KitsTemplate/KitsTemplateEditor';
import { htmlToPlainText } from 'pages/Radar/RadarKits/utils';
import useReminderTemplate from 'pages/Dashboard/Kits/hooks/use-reminder-template.hook';

type CreationModalProps = {
  showModal: (show: boolean) => void;
  open: boolean;
  categories: any[];
  frequencies: any[];
  propertyTypes: string[];
  newKitHandler: (kit: any) => void;
  updateKitHandler?: (kit: any) => void;
  cloneKit?: {
    Id: number;
    Name: string;
    InternalName: string;
    Category: string;
    Frequencies: any[];
    EligibleFrequencies?: Array<any>;
    EligiblePropertyTypes: string;
    InAppReminder: boolean;
    UseDynamicTemplate: boolean;
    EligibleLocations: any[];
    RestrictedLocations: any[];
    Notes: string;
    VideoUrl: string;
  };
  setCloneId?: (id: number) => void;
  isUpdate?: boolean;
};

function CreationModal({
  showModal,
  open,
  categories,
  frequencies,
  propertyTypes,
  newKitHandler,
  cloneKit,
  setCloneId,
  isUpdate = false,
}: CreationModalProps) {
  const [showAddDetails, setShowAddDetails] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [kitTemplate, setKitTemplate] = useState<any>(null);
  const [kitTemplateLoading, setKitTemplateLoading] = useState(false);

  const {
    reminderTemplate,
    getReminderTemplate,
    loading: reminderTemplateLoading,
  } = useReminderTemplate();

  const formik = useFormik({
    initialValues: {
      cloneKitId: cloneKit?.Id,
      name: cloneKit?.Name || '',
      internalName: cloneKit?.InternalName || '',
      category: cloneKit?.Category || '',
      frequencies:
        cloneKit?.Frequencies ||
        (cloneKit?.EligibleFrequencies || [])?.map(({ Id, ...rest }) => ({
          ...rest,
          isExisting: true,
        })),
      propertyTypes: cloneKit?.EligiblePropertyTypes?.replace(/[{}"]/g, '')?.split(',') || [],
      inAppReminder: cloneKit?.InAppReminder || false,
      useDynamicTemplate: cloneKit?.UseDynamicTemplate || false,
      eligibleLocations: cloneKit?.EligibleLocations || [],
      restrictedLocations: cloneKit?.RestrictedLocations || [],
      notes: cloneKit?.Notes || '',
      videoUrl: cloneKit?.VideoUrl || '',
      reminder: {
        Template: reminderTemplate?.Template || '',
        AllowedVariables: reminderTemplate?.AllowedVariables || [],
      },
      template: {
        html: kitTemplate?.html || '',
        css: kitTemplate?.css || '',
        templateMeta: {
          projectJSON: kitTemplate?.templateMeta?.projectJSON || null,
        },
      },
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.name) errors.name = 'Name is required';
      if (!values.category) errors.category = 'Category is required';
      if (!values.frequencies.length) errors.frequencies = 'At least one frequency is required';
      if (!values.propertyTypes.length)
        errors.propertyTypes = 'At least one property type is required';
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          ...values,
          eligibleLocations: values.eligibleLocations.map(
            ({ PlaceName, ProviderPlaceId, ...rest }) => ({ PlaceName, ProviderPlaceId }),
          ),
          restrictedLocations: values.restrictedLocations.map(
            ({ PlaceName, ProviderPlaceId, ...rest }) => ({ PlaceName, ProviderPlaceId }),
          ),
          frequencies: values.frequencies
            .filter((freq) => !!freq.FrequencyName)
            .map(({ isExisting, ...rest }) => rest),
        };

        if (isEdit) {
          const updatedKit = await updateKitById(cloneKit?.Id, payload);
          newKitHandler(updatedKit.data.result);
        } else {
          const newKit = await createNewKitByAdmin(payload);
          newKitHandler(newKit.data.result);
        }

        formik.resetForm();
        showModal(false);
      } catch (err) {
        showErrorMessage(`${(err as any)?.response?.data?.message || 'Something went wrong'}`);
      }
    },
  });

  const updateLocation = (result, prepareData, isEligible = true) => {
    const fieldName = isEligible ? 'eligibleLocations' : 'restrictedLocations';

    if (result?.deleteItemId) {
      formik.setFieldValue(
        fieldName,
        formik.values[fieldName].filter((loc) => loc.ProviderPlaceId !== result.deleteItemId),
      );
    } else if (prepareData) {
      formik.setFieldValue(fieldName, prepareData);
    }
  };

  const fetchTemplateByKitId = useCallback(async (kitId, cb?) => {
    try {
      setKitTemplateLoading(true);
      const templates = await getKitsTemplatesByKitId(kitId);
      if (templates?.data?.result?.length > 0) {
        const template = templates?.data?.result[0];
        setKitTemplate(template);
        cb?.(template);
      }
    } catch (err) {
      showErrorMessage(`${err}`);
    } finally {
      setKitTemplateLoading(false);
    }
  }, []);

  useEffect(() => {
    // load email template and reminder template
    if (cloneKit?.Id) {
      fetchTemplateByKitId(cloneKit?.Id, (data) => {
        if (data?.html && data?.css) {
          formik.setFieldValue('template', {
            html: data?.html || '',
            css: data?.css || '',
            templateMeta: {
              projectJSON: data?.templateMeta?.projectJSON || null,
            },
          });
        }
      });
      getReminderTemplate(cloneKit?.Id, (data) => {
        if (data?.Template) {
          formik.setFieldValue('reminder', {
            Template: data?.Template || '',
            AllowedVariables: data?.AllowedVariables || [],
          });
        }
      });
    }
  }, [cloneKit?.Id]);

  const isLoading = reminderTemplateLoading?.get || kitTemplateLoading;
  const isEdit = cloneKit?.Id && isUpdate;

  const saveAs = () => {
    if (cloneKit?.Id) {
      setCloneId?.(cloneKit?.Id);
      showModal(false);
    }
  };

  return (
    <>
      <DetailsModal
        open={showAddDetails}
        showModal={setShowAddDetails}
        initialValue={formik.values.notes}
        onSave={(value) => formik.setFieldValue('notes', value)}
      />
      <ReminderModal
        open={showReminderModal}
        showModal={setShowReminderModal}
        initialValue={formik.values.reminder}
        onSave={(value) => formik.setFieldValue('reminder', value)}
      />
      <KitsTemplateModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSave={(template: KitsTemplateModel) => {
          formik.setFieldValue('template', template);
          setShowTemplateModal(false);
        }}
        onDelete={() => {
          formik.setFieldValue('template', {
            html: '',
            css: '',
            templateMeta: {
              projectJSON: null,
            },
          });
          setShowTemplateModal(false);
        }}
        initialValue={formik.values.template}
      />
      <Modal
        footer={null}
        width={992}
        open={open}
        onCancel={() => showModal(false)}
        title={
          <span className={styles.headerTitle}>
            <span className={styles.headerText}>
              {cloneKit ? `${isEdit ? 'Edit' : 'Copy'} ${cloneKit.Name}` : `New KIT`}
            </span>
          </span>
        }
        className={styles.newKitModal}
        closeIcon={<span className={styles.closeIcon}>×</span>}
      >
        {isLoading ? (
          <div className={styles.loaderContainer}>
            <Spin size="large" className={styles.loader} />
          </div>
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} className={styles.content}>
              <div className={styles.formColumns}>
                <div className={styles.leftColumn}>
                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Name</p>
                    <Input
                      inputClassName={styles.input}
                      className={styles.input}
                      variant={Input.LIGHT_ROUNDED}
                      placeholder="Enter Name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      maxLength={10000}
                    />
                    {formik.errors.name && formik.touched.name && (
                      <div className={styles.error}>{formik.errors.name}</div>
                    )}
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Internal Name</p>
                    <Input
                      className={styles.input}
                      variant={Input.LIGHT_ROUNDED}
                      placeholder="Enter Internal Name"
                      name="internalName"
                      value={formik.values.internalName}
                      onChange={formik.handleChange}
                      maxLength={100}
                    />
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Category</p>
                    <Select
                      large
                      disabled={false}
                      showArrow={true}
                      placeholder="Select Category"
                      getPopupContainer={(triggerNode) => triggerNode}
                      menuItemSelectedIcon={<CheckBox className={styles.checkBoxIcon} />}
                      className={classNames(styles.selector, styles.categorySelect)}
                      options={categories.map((el) => ({ label: el.Name, value: el.Name }))}
                      onChange={(id) => formik.setFieldValue('category', id)}
                      value={formik.values.category}
                    />
                    {formik.errors.category && formik.touched.category && (
                      <div className={styles.error}>{formik.errors.category}</div>
                    )}
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Property Types</p>
                    <Select
                      large
                      mode="multiple"
                      placeholder="Select Property Types"
                      getPopupContainer={(triggerNode) => triggerNode}
                      className={styles.selector}
                      options={propertyTypes.map((el) => ({ label: el, value: el }))}
                      onChange={(val) => formik.setFieldValue('propertyTypes', val)}
                      value={formik.values.propertyTypes}
                    />
                    {formik.errors.propertyTypes && formik.touched.propertyTypes && (
                      <div className={styles.error}>{formik.errors.propertyTypes}</div>
                    )}
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Eligible Locations</p>
                    <Locations
                      multiple
                      allowStates={true}
                      getStates={true}
                      onResult={(result, prepareData) => updateLocation(result, prepareData, true)}
                      value={
                        formik.values.eligibleLocations?.length
                          ? formik.values.eligibleLocations
                          : undefined
                      }
                      placeholder="Enter City, State, or County"
                      activeInputClassName={styles.activeInputClassName}
                      valuesWrapperClassName={styles.valuesWrapperClassName}
                      placeholderClassName={styles.placeholderClass}
                      valuesContainerClassName={styles.valuesContainer}
                      searchIcon={false}
                      variant={Locations.SQUARE}
                      customPlaceName={({ address }) =>
                        address?.split?.(',')?.slice(0, 2)?.join(',')
                      }
                      showOnlyCityAndState={true}
                    />
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Restricted Locations</p>
                    <Locations
                      multiple
                      allowStates={true}
                      getStates={true}
                      onResult={(result, prepareData) => updateLocation(result, prepareData, false)}
                      value={
                        formik.values.restrictedLocations?.length
                          ? formik.values.restrictedLocations
                          : undefined
                      }
                      placeholder="Enter City, State, or County"
                      activeInputClassName={styles.activeInputClassName}
                      valuesWrapperClassName={styles.valuesWrapperClassName}
                      placeholderClassName={styles.placeholderClass}
                      valuesContainerClassName={styles.valuesContainer}
                      searchIcon={false}
                      variant={Locations.SQUARE}
                      customPlaceName={({ address }) =>
                        address?.split?.(',')?.slice(0, 2)?.join(',')
                      }
                      showOnlyCityAndState={true}
                    />
                  </div>
                </div>

                <div className={styles.rightColumn}>
                  <div className={styles.toggleField}>
                    <div className={styles.toggleHeader}>
                      <p className={styles.sectionLabel}>In-App Reminder</p>
                      <Switch
                        checked={formik.values.inAppReminder}
                        onChange={(checked) => formik.setFieldValue('inAppReminder', checked)}
                      />
                    </div>
                    <div className={styles.addLink} onClick={() => setShowReminderModal(true)}>
                      {htmlToPlainText(formik.values.reminder.Template) ? 'Edit Reminder' : '+ Add'}
                    </div>
                  </div>

                  <div className={styles.toggleField}>
                    <div className={styles.toggleHeader}>
                      <p className={styles.sectionLabel}>Use Dynamic Template</p>
                      <Switch
                        checked={formik.values.useDynamicTemplate}
                        onChange={(checked) => formik.setFieldValue('useDynamicTemplate', checked)}
                      />
                    </div>
                    <div className={styles.addLink} onClick={() => setShowTemplateModal(true)}>
                      {htmlToPlainText(formik.values.template?.html)
                        ? 'Edit Template'
                        : '+ Add Template'}
                    </div>
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Frequencies</p>
                    {formik.errors.frequencies && formik.touched.frequencies && (
                      <div className={styles.error}>{formik.errors.frequencies}</div>
                    )}
                    <div className={styles.frequencyContainer}>
                      <FrequencyConfigurator
                        values={formik.values}
                        setFieldValue={formik.setFieldValue}
                        helpers={{ setValues: formik.setValues, setTouched: formik.setTouched }}
                        isEdit={isEdit}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <p className={styles.sectionLabel}>Video URL</p>
                    <Input
                      className={styles.input}
                      variant={Input.LIGHT_ROUNDED}
                      placeholder="Enter Video URL"
                      name="videoUrl"
                      value={formik.values.videoUrl}
                      onChange={formik.handleChange}
                    />
                  </div>

                  {htmlToPlainText(formik.values.notes) ? (
                    <>
                      <div
                        className={styles.detailsEditPill}
                        onClick={() => setShowAddDetails(true)}
                      >
                        <PenIcon />
                        Edit Details
                      </div>
                    </>
                  ) : (
                    <div className={styles.detailsField}>
                      <Button
                        type="default"
                        className={styles.addDetailsBtn}
                        onClick={() => setShowAddDetails(true)}
                      >
                        <PlusIcon />
                        Add Details
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {isEdit ? (
                <div className={styles.editButtonContainer}>
                  <Button
                    type="default"
                    className={styles.saveAsButton}
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                    onClick={saveAs}
                  >
                    Save As
                  </Button>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className={styles.updateButton}
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                  >
                    Update
                  </Button>
                </div>
              ) : (
                <div className={styles.createButtonContainer}>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className={styles.createButton}
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                  >
                    Create
                  </Button>
                </div>
              )}
            </form>
          </FormikProvider>
        )}
      </Modal>
    </>
  );
}

export default CreationModal;
